import React from "react";
import { classNames, formatDueLabel, primaryContactEmail, formatDateTime } from "./utils";
import { describeFollowUpReason } from "../hooks/useSmartView";

function Section({ title, count, onSeeAll, seeAllLabel, children }) {
  if (!count) return null;
  return (
    <section className="rounded-xl border border-zinc-200 bg-white shadow-card">
      <header className="flex items-center justify-between border-b border-zinc-100 px-4 py-2.5">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-zinc-900">{title}</h3>
          <span className="rounded-full bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-zinc-600">
            {count}
          </span>
        </div>
        {onSeeAll ? (
          <button
            onClick={onSeeAll}
            className="text-xs font-medium text-indigo-600 hover:underline"
          >
            {seeAllLabel || "See all"}
          </button>
        ) : null}
      </header>
      <div>{children}</div>
    </section>
  );
}

export default function TodayView({
  followUps,
  queue,
  replies,
  onOpenLead,
  onSendQueueItem,
  onSnooze,
  onSmartViewChange,
  gmailConnected,
}) {
  const nothingToDo = !followUps.length && !queue.length && !replies.length;

  if (nothingToDo) {
    return (
      <div className="flex h-full flex-col items-center justify-center px-6 text-center">
        <p className="text-base font-semibold text-zinc-900">Inbox zero.</p>
        <p className="mt-1 max-w-sm text-sm text-zinc-500">
          You're caught up for today. Check back tomorrow or jump into All leads to enroll more outreach.
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto bg-zinc-50/40 px-5 py-4">
      <div className="grid gap-3">
        <Section
          title="Due follow-ups"
          count={followUps.length}
          onSeeAll={() => onSmartViewChange?.("followups")}
        >
          <div>
            {followUps.slice(0, 6).map((row) => {
              const cl = row.campaignLead || {};
              const overdue = cl.nextActionAt && new Date(cl.nextActionAt) < new Date();
              return (
                <div
                  key={cl._id}
                  onClick={() => onOpenLead?.(row)}
                  className="grid cursor-pointer grid-cols-[1.5fr,1.2fr,0.7fr,auto] items-center gap-3 border-t border-zinc-100 px-4 py-2.5 first:border-t-0 hover:bg-zinc-50"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-zinc-900">
                      {row.lead?.company_name || "Untitled lead"}
                    </p>
                    <p className="truncate text-xs text-zinc-500">
                      {primaryContactEmail(row.lead) || row.lead?.website || "—"}
                    </p>
                  </div>
                  <span className="inline-flex max-w-max items-center rounded-md bg-zinc-100 px-2 py-0.5 text-[11px] font-medium text-zinc-700">
                    {describeFollowUpReason(row)}
                  </span>
                  <span
                    className={classNames(
                      "text-xs tabular-nums",
                      overdue ? "font-medium text-red-600" : "text-zinc-600"
                    )}
                  >
                    {formatDueLabel(cl.nextActionAt)}
                  </span>
                  <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => onSnooze?.(row, nextDay())}
                      className="rounded-md border border-zinc-200 bg-white px-2 py-1 text-[11px] font-medium text-zinc-600 hover:bg-zinc-50"
                    >
                      Tomorrow
                    </button>
                    <button
                      onClick={() => onOpenLead?.(row)}
                      className="rounded-md bg-indigo-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-indigo-700"
                    >
                      Open
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </Section>

        <Section
          title="Pending send queue"
          count={queue.length}
          onSeeAll={() => onSmartViewChange?.("queue")}
        >
          <div>
            {queue.slice(0, 6).map((row) => {
              const overdue =
                row.queueItem?.dueAt && new Date(row.queueItem.dueAt) < new Date();
              return (
                <div
                  key={row.queueItem._id}
                  onClick={() => onSmartViewChange?.("queue")}
                  className="grid cursor-pointer grid-cols-[1.5fr,1.2fr,0.7fr,auto] items-center gap-3 border-t border-zinc-100 px-4 py-2.5 first:border-t-0 hover:bg-zinc-50"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-zinc-900">
                      {row.lead?.company_name || "Lead"}
                    </p>
                    <p className="truncate text-xs text-zinc-500">
                      {row.queueItem.renderedSubject}
                    </p>
                  </div>
                  <span className="text-xs text-zinc-600">
                    Step {row.queueItem.stepIndex + 1}
                  </span>
                  <span
                    className={classNames(
                      "text-xs tabular-nums",
                      overdue ? "font-medium text-red-600" : "text-zinc-600"
                    )}
                  >
                    {formatDueLabel(row.queueItem.dueAt)}
                  </span>
                  <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => onSendQueueItem?.(row.queueItem._id)}
                      disabled={!gmailConnected}
                      className="rounded-md bg-zinc-900 px-2 py-1 text-[11px] font-medium text-white hover:bg-zinc-800 disabled:cursor-not-allowed disabled:bg-zinc-300"
                    >
                      Send now
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </Section>

        <Section
          title="Recent replies"
          count={replies.length}
          onSeeAll={() => onSmartViewChange?.("replies")}
        >
          <div>
            {replies.slice(0, 5).map((row) => {
              const snippet = (row.activity?.text || "")
                .replace(/\s+/g, " ")
                .trim()
                .slice(0, 140);
              return (
                <button
                  key={row.activity._id}
                  onClick={() =>
                    row.lead
                      ? onOpenLead?.({ lead: row.lead, campaignLead: row.campaignLead })
                      : null
                  }
                  className="flex w-full items-start gap-3 border-t border-zinc-100 px-4 py-2.5 text-left first:border-t-0 hover:bg-zinc-50"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="truncate text-sm font-semibold text-zinc-900">
                        {row.lead?.company_name || row.activity?.from || "Reply"}
                      </p>
                      <p className="shrink-0 text-xs text-zinc-400 tabular-nums">
                        {formatDateTime(row.activity?.occurredAt)}
                      </p>
                    </div>
                    <p className="mt-0.5 truncate text-xs text-zinc-500">
                      {row.activity?.subject || "No subject"}
                    </p>
                    <p className="mt-1 line-clamp-1 text-sm text-zinc-700">
                      {snippet || "No preview available."}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </Section>
      </div>
    </div>
  );
}

function nextDay() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return d;
}
